import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useCarers from '@/api/useCarers.ts';
import Default from '@/layouts/Default/Default.tsx';
import { useDefaultLayoutContext } from '@/layouts/Default/DefaultLayoutProvider.tsx';
import { Button, Toggle } from 'react-daisyui';
import CheckboxGroup from '@/components/functional/CheckboxInput/CheckboxGroup.tsx';
import RangeSlider from '@/components/functional/RangeInput/RangeSlider.tsx';
import Calendar from '@/components/shared/Calendar/Calendar.tsx';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import { useAuthContext } from '@/context/AuthContext.tsx';
import useFamilyOnboarding from '@/api/useFamilyOnboarding.ts';
import { distanceFromCoords, normaliseUkPostcode } from '@/utils/postcodeDistance.ts';
import { usePostcodeCoords, usePostcodeCoordsMap } from '@/api/usePostcodeCoords.ts';
import { trackEvent } from '@/utils/analytics.ts';
import { SKILL_CATEGORIES, CARE_TYPE_OPTIONS } from '@/constants/careSkills.ts';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

const CarersPage: FC = () => {
  const navigate = useNavigate();
  const { data: carers, isLoading, isError } = useCarers();
  const { setIsDrawerOpen } = useDefaultLayoutContext();
  const { t } = useTranslation();
  const { session } = useAuthContext();
  const { data: familyData } = useFamilyOnboarding(session?.user?.email);
  const userPostcode = familyData?.location || '';

  // Resolve the family's home postcode to coordinates (postcodes.io, cached).
  const {
    coords: userCoords,
    isLoading: isUserCoordsLoading,
    isUnknown: isUserPostcodeUnknown,
    isInvalid: isUserPostcodeInvalid,
  } = usePostcodeCoords(userPostcode);

  // Resolve every visible carer's service-area postcode in parallel (cached).
  const carerPostcodes = useMemo(
    () => (carers ?? []).map((c) => c.service_area ?? ''),
    [carers]
  );
  const { coordsByPostcode: carerCoordsMap, isLoading: areCarerCoordsLoading } =
    usePostcodeCoordsMap(carerPostcodes);

  // Filter states
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [radiusKm, setRadiusKm] = useState(50);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [hourlyRateRange, setHourlyRateRange] = useState<[number, number]>([0, 100]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [gender, setGender] = useState<string>('');
  const [selectedCareTypes, setSelectedCareTypes] = useState<string[]>([]);
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const searchTracked = useRef(false);

  const toggleCategory = (value: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  useEffect(() => {
    if (!searchTracked.current && carers && carers.length >= 0) {
      trackEvent({ name: 'search_performed' });
      searchTracked.current = true;
    }
  }, [carers]);

  const handleClearFilters = () => {
    setVerifiedOnly(false);
    setRadiusKm(50);
    setSelectedSkills([]);
    setHourlyRateRange([0, 100]);
    setStartDate(null);
    setEndDate(null);
    setGender('');
    setSelectedCareTypes([]);
  };

  const handleDateSelect = (date: Date, isStart: boolean) => {
    if (isStart) {
      setStartDate(date);
      setEndDate(null);
    } else {
      if (startDate && date < startDate) {
        setEndDate(startDate);
        setStartDate(date);
      } else {
        setEndDate(date);
      }
    }
  };

  const hasActiveFilters = 
    verifiedOnly ||
    radiusKm !== 50 ||
    selectedSkills.length > 0 || 
    selectedCareTypes.length > 0 ||
    hourlyRateRange[0] !== 0 || 
    hourlyRateRange[1] !== 100 ||
    startDate !== null ||
    gender !== '';

  // Filter carers
  const filteredCarers = useMemo(() => {
    if (!carers) return [];
    
    return carers.filter((carer) => {
      // Verified only filter
      if (verifiedOnly && carer.verification_status !== 'verified') {
        return false;
      }
      
      // Radius filter — only apply when the family's postcode resolved to
      // real coordinates. If the carer's postcode hasn't resolved yet (or
      // is unknown), exclude them so unverified locations don't slip
      // through the filter.
      if (userCoords) {
        if (!carer.service_area) return false;
        const carerCoords = carerCoordsMap.get(normaliseUkPostcode(carer.service_area));
        if (!carerCoords) {
          // Still loading or postcode unrecognised — exclude rather than
          // silently include (the previous behaviour incorrectly let
          // every unknown postcode through).
          if (!areCarerCoordsLoading) return false;
          return false;
        }
        const distance = distanceFromCoords(userCoords, carerCoords);
        if (distance === null || distance > radiusKm) return false;
      }
      
      // Care type filter
      if (selectedCareTypes.length > 0) {
        const carerCareTypes = carer.care_types || [];
        const hasMatchingCareType = selectedCareTypes.some(ct => carerCareTypes.includes(ct));
        if (!hasMatchingCareType) return false;
      }

      // Skills filter
      if (selectedSkills.length > 0) {
        const carerSkills = carer.skills || [];
        const hasMatchingSkill = selectedSkills.some(skill => carerSkills.includes(skill));
        if (!hasMatchingSkill) return false;
      }
      
      // Hourly rate filter
      if (carer.hourly_rate !== undefined) {
        if (carer.hourly_rate < hourlyRateRange[0] || carer.hourly_rate > hourlyRateRange[1]) {
          return false;
        }
      }
      
      // Availability dates filter
      if (startDate || endDate) {
        const carerStart = carer.availability_dates?.startDate 
          ? new Date(carer.availability_dates.startDate) 
          : null;
        const carerEnd = carer.availability_dates?.endDate 
          ? new Date(carer.availability_dates.endDate) 
          : null;
        
        // Carer has no availability set - exclude
        if (!carerStart || !carerEnd) return false;
        
        // Check if selected range overlaps with carer's availability
        const filterStart = startDate || endDate;
        const filterEnd = endDate || startDate;
        
        if (filterStart! > carerEnd || filterEnd! < carerStart) {
          return false;
        }
      }
      
      return true;
    });
  }, [
    carers,
    selectedCareTypes,
    selectedSkills,
    hourlyRateRange,
    verifiedOnly,
    startDate,
    endDate,
    radiusKm,
    userCoords,
    carerCoordsMap,
    areCarerCoordsLoading,
  ]);

  return (
    <>
      <div className="flex flex-col gap-6 w-full">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">{t('carersPage.title')}</h1>
          <div className="relative">
            <Button
              onClick={() => setIsDrawerOpen(true)}
              className="bg-black text-white border-none hover:opacity-80 rounded-[12px] h-[48px] px-4 flex items-center justify-center gap-2 min-h-0"
            >
              <img src="/assets/filters.svg" alt="" />
              <span className="text-sm font-semibold">Filters</span>
            </Button>
            {hasActiveFilters && (
              <span className="absolute -top-2 -right-2 bg-[#FED32C] text-black rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                {(verifiedOnly ? 1 : 0) + (radiusKm !== 50 ? 1 : 0) + selectedCareTypes.length + selectedSkills.length + (startDate ? 1 : 0) + (gender ? 1 : 0)}
              </span>
            )}
          </div>
        </div>

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          {Array.from({ length: 18 }).map((_, index) => (
            <div key={index} className="flex flex-col items-center gap-3 bg-[#F6F6F4] rounded-[16px] px-10 py-6 animate-pulse">
              <div className="w-[80px] h-[80px] bg-gray-300 rounded-full" />
              <div className="w-24 h-4 bg-gray-300 rounded" />
            </div>
          ))}
        </div>
      )}

      {isError && (
        <div className="text-center text-red-500 py-8">
          <p>{t('carersPage.errorLoading')}</p>
        </div>
      )}

      {!isLoading && !isError && carers && carers.length === 0 && (
        <div className="bg-[#F6F6F4] rounded-lg py-6 flex items-center justify-center h-full w-[637px] mx-auto mt-[100px]">
          <p className="text-base font-semibold text-center text-black">
            {t('carersPage.noCarers')}
          </p>
        </div>
      )}

      {!isLoading && !isError && filteredCarers.length === 0 && carers && carers.length > 0 && (
        <div className="bg-[#F6F6F4] rounded-lg py-6 flex items-center justify-center h-full w-[637px] mx-auto mt-[100px]">
          <p className="text-base font-semibold text-center text-black">
            {t('carersPage.noMatch')}
          </p>
        </div>
      )}

      {!isLoading && !isError && filteredCarers && filteredCarers.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 mt-2 sm:mt-0">
          {filteredCarers.map((carer) => (
            <div
              key={carer.id}
              onClick={() => navigate(`/carers/${carer.id}`)}
              className="flex flex-col w-full items-center gap-3 bg-[#F6F6F4] rounded-[16px] px-4 py-6 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="relative">
                {carer.photo_url ? (
                  <img
                    src={carer.photo_url}
                    alt={carer.name}
                    className="w-[80px] h-[80px] rounded-full object-contain"
                  />
                ) : (
                  <div className="w-[80px] h-[80px] rounded-full bg-gray-200 flex items-center justify-center text-2xl font-semibold text-gray-500">
                    {carer.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="absolute top-[3px] left-[-10px] w-8 h-8 bg-black rounded-full flex items-center justify-center">
                  <img src="/assets/i.svg" alt="info"  />
                </div>
              </div>
              <p className="text-sm font-bold text-center">{carer.name}</p>
            </div>
          ))}
        </div>
      )}
      </div>

      <Default.Drawer>
        <div className="w-[340px] max-w-[90vw] flex flex-col gap-6 pb-6">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold mb-1">{t('filtersDrawer.title')}</h2>
              <p className="text-sm text-gray-600">{t('filtersDrawer.subtitle')}</p>
            </div>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium underline"
              >
                {t('filtersDrawer.clearAll')}
              </button>
            )}
          </div>
          
          {/* Verified Only Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">{t('filtersDrawer.verifiedOnly')}</span>
            <Toggle
              checked={verifiedOnly}
              onChange={(e) => setVerifiedOnly(e.target.checked)}
              color="primary"
              size="sm"
            />
          </div>

          {/* Radius Slider */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-600">
              {userPostcode ? t('filtersDrawer.radiusFrom', { postcode: userPostcode }) : t('filtersDrawer.radius')}: {radiusKm}km
            </label>
            {userPostcode && isUserPostcodeInvalid && (
              <p className="text-xs text-red-600">
                {t('filtersDrawer.postcodeInvalid')}
              </p>
            )}
            {userPostcode && !isUserPostcodeInvalid && isUserCoordsLoading && (
              <p className="text-xs text-gray-500">{t('filtersDrawer.postcodeLoading')}</p>
            )}
            {userPostcode && !isUserPostcodeInvalid && isUserPostcodeUnknown && (
              <p className="text-xs text-red-600">
                {t('filtersDrawer.postcodeUnknown', { postcode: userPostcode })}
              </p>
            )}
            <input
              type="range"
              min={0}
              max={50}
              step={1}
              value={radiusKm}
              onChange={(e) => setRadiusKm(Number(e.target.value))}
              className="range range-sm"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>0km</span>
              <span>25km</span>
              <span>50km</span>
            </div>
          </div>

          {/* Care Type Filter */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-600">{t('filtersDrawer.careType')}</label>
            <CheckboxGroup.Group value={selectedCareTypes} onChange={setSelectedCareTypes}>
              <div className="flex flex-col gap-1">
                {CARE_TYPE_OPTIONS.map((option) => (
                  <CheckboxGroup.Input
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  />
                ))}
              </div>
            </CheckboxGroup.Group>
          </div>

          {/* Skills Filter — grouped by category, collapsible */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-600">{t('filtersDrawer.skills')}</label>
            <CheckboxGroup.Group value={selectedSkills} onChange={setSelectedSkills}>
              <div className="flex flex-col gap-1">
                {SKILL_CATEGORIES.map((cat) => {
                  const isOpen = openCategories.has(cat.value);
                  const activeCount = cat.skills.filter((s) => selectedSkills.includes(s.value)).length;
                  return (
                    <div key={cat.value} className="border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        type="button"
                        onClick={() => toggleCategory(cat.value)}
                        className="w-full flex items-center justify-between px-4 py-3 bg-white hover:bg-gray-50 transition-colors text-left cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-700">{cat.label}</span>
                          {activeCount > 0 && (
                            <span className="bg-black text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                              {activeCount}
                            </span>
                          )}
                        </div>
                        <ChevronDownIcon className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      <div className={`grid transition-all duration-200 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
                        <div className="overflow-hidden">
                          <div className="px-4 py-3 flex flex-col gap-1 bg-gray-50 border-t border-gray-100">
                            {cat.skills.map((skill) => (
                              <CheckboxGroup.Input
                                key={skill.value}
                                value={skill.value}
                                label={skill.label}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CheckboxGroup.Group>
          </div>

          {/* Hourly Rate Range */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-medium text-gray-600">{t('filtersDrawer.hourlyRate')}</label>
            <RangeSlider
              min={0}
              max={100}
              step={5}
              value={hourlyRateRange}
              onInput={(values) => setHourlyRateRange(values as [number, number])}
              showValues={false}
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>£{hourlyRateRange[0]}</span>
              <span>£{hourlyRateRange[1]}</span>
            </div>
          </div>

          {/* Calendar */}
          <div className="flex flex-col gap-3">
            <Calendar
              startDate={startDate}
              endDate={endDate}
              onDateSelect={handleDateSelect}
            />
          </div>

          {/* Gender Dropdown */}
          <div className="flex flex-col gap-3">
            <SelectInput
              value={gender}
              onChange={setGender}
              options={[
                { value: '', label: t('filtersDrawer.genderOther') },
                { value: 'male', label: t('filtersDrawer.genderMale') },
                { value: 'female', label: t('filtersDrawer.genderFemale') },
              ]}
              placeholder={t('filtersDrawer.gender')}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 mt-4">
            <Button
              onClick={() => setIsDrawerOpen(false)}
              className="w-full bg-black text-white border-none hover:opacity-80 rounded-[12px] h-12"
            >
              {t('filtersDrawer.viewCarerList')}
            </Button>
            {hasActiveFilters && (
              <Button
                onClick={handleClearFilters}
                className="w-full bg-white text-black border border-gray-300 hover:bg-gray-50 rounded-[12px] h-12"
              >
                {t('filtersDrawer.clearFilters')}
              </Button>
            )}
          </div>
        </div>
      </Default.Drawer>
    </>
  );
};

export default CarersPage;
